import React, { useEffect, useRef, useState } from "react";
import { connect } from "react-redux";
import { useBoolean } from "react-hanger";
import { Helmet } from "react-helmet-async";
import fileDownload from "js-file-download";
import SetInterval from "set-interval";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CircularProgress from "@mui/material/CircularProgress";
import Container from "@mui/material/Container";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Link from "@mui/material/Link";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Typography from "@mui/material/Typography";
import { Editor } from "@tinymce/tinymce-react";

import { BACKEND_URL, siteConstants } from "../../../constants";
import LinearProgressWithLabel from "../../LinearProgressWithLabel";
import { alertActions, csActions, ordersActions } from "../../../redux/actions";
import { csServices } from "../../../services";
import PdfIcon from "../../../assets/images/pdf.png";

const AssessmentReportPage = (props) => {
  const editorRef = useRef(null);
  const [selectedTemplateId, setSelectedTemplateId] = useState("");
  const [report, setReport] = useState(null);
  const [processProgress, setProcessProgress] = useState(0);
  const isCreatingAssessmentReport = useBoolean(false);
  const isCreatingAssessmentReportPreview = useBoolean(false);
  const [assessmentReportPreview, setAssessmentReportPreview] = useState("");

  const handleAssessmentReportTemplateChange = (e) => {
    setSelectedTemplateId(e.target.value);
  }

  const handleCreateAssessmentReport = (e) => {
    props.alertReset();

    if (editorRef.current) {
      isCreatingAssessmentReport.setTrue();
      const content = editorRef.current.getContent();
      console.log(content);
      csServices.createAssessmentReport(report.id, selectedTemplateId, content)
        .then(response => {
          isCreatingAssessmentReport.setFalse();
          fileDownload(response.data, `${report.client_name}.docx`);
        })
        .catch(error => {
          isCreatingAssessmentReport.setFalse();
          if (error.response)
            props.alertError(error.response.data.detail || siteConstants.ERROR_MSG_SERVER_COMMUNICATION);
          else
            props.alertError(siteConstants.ERROR_MSG_SERVER_COMMUNICATION);
        });
    }
  }

  const pollProcessingStatus = () => {
    csServices.retrieveReport(props.report.id)
      .then(response => {
        const _report = response.data;
        if (
          _report.status !== "ready for review"
          && _report.status !== "reviewed"
          && _report.status !== "assessment generated"
        ) {
          setProcessProgress(_report.process_progress);
        } else {
          SetInterval.clear("pollProcessingStatus");
          setReport(_report);
        }
      })
      .catch(error => {
        if (error.response)
          props.alertError(error.response.data.detail || siteConstants.ERROR_MSG_SERVER_COMMUNICATION);
        else
          props.alertError(siteConstants.ERROR_MSG_SERVER_COMMUNICATION);
      });
  }

  useEffect(() => {
    props.alertReset();

    if (
      report
      && (report.visitor?.source !== siteConstants.SOURCE_COUPLECOACHING)
      && selectedTemplateId
    ) {
      setAssessmentReportPreview("")
      props.createAssessmentReportPreview(report.id, selectedTemplateId);
    }
  }, [report, selectedTemplateId]);

  useEffect(() => {
    if (props.report) {
      if (
        props.report.status === "ready for review"
        || props.report.status === "reviewed"
        || props.report.status === "assessment generated"
      )
        setReport(props.report);
      else
        SetInterval.start(pollProcessingStatus, 2000, "pollProcessingStatus");
    }
  }, [props.report])

  useEffect(() => {
    if (
      report
      && (report.visitor?.source !== siteConstants.SOURCE_COUPLECOACHING)
      && props.assessmentReportTemplates
    ) {
      if (report.visitor)  // use "Without CS text" template
        setSelectedTemplateId(props.assessmentReportTemplates[1].id);
      else  // use "Default" template
        setSelectedTemplateId(props.assessmentReportTemplates[1].id);
    }
  }, [report, props.assessmentReportTemplates]);

  useEffect(() => {
    if (props.assessmentReportPreview)
      setAssessmentReportPreview(props.assessmentReportPreview);
  }, [props.assessmentReportPreview]);

  useEffect(() => {
    if (assessmentReportPreview)
      isCreatingAssessmentReportPreview.setFalse()
    else
      isCreatingAssessmentReportPreview.setTrue()
  }, [assessmentReportPreview]);

  useEffect(() => {
    props.alertReset();

    const queryStrings = new URLSearchParams(window.location.search);
    const reportId = queryStrings.get("id", "");
    if (reportId)
      props.retrieveReport(reportId);

    props.listAssessmentReportTemplates();

    return () => SetInterval.clear("pollProcessingStatus");
  }, []);

  return (
    <>
      <Helmet>
        <title>Assessment Report</title>
      </Helmet>

      <Container
        maxWidth="xl"
        sx={{
          marginTop: "30px",
          textAlign: "left",
        }}
      >
        <Typography variant="h4">Assessment Report</Typography>
        {
          props.alert.message &&
          <Alert variant="outlined" severity={ props.alert.type }>
            { props.alert.message }
          </Alert>
        }
        {props.report &&
          <Card variant="outlined" sx={{ marginTop: "20px" }}>
            <CardContent>
              <Typography sx={{marginBottom: "8px"}}>Source: {props.report.visitor?.source || "cs3"}</Typography>
              <Box
                sx={{
                  display: "flex",
                }}
              >
                <Typography sx={{flex: 1}}>Client Name: {props.report.client_name}</Typography>
                <Link
                  href={ `${BACKEND_URL}${props.report.file}` }
                  target="_blank"
                  rel="noopener"
                >
                  <img src={PdfIcon} style={{ width: "24px" }} />
                </Link>
              </Box>

              {props.report.partner &&
                <Box
                  sx={{
                    display: "flex",
                  }}
                >
                  {props.report.visitor?.source === siteConstants.SOURCE_COUPLECOACHING &&
                    <Typography sx={{flex: 1}}>
                      Partner Name: {props.report.partner.client_name}
                    </Typography>
                  }
                  {props.report.visitor?.source === siteConstants.SOURCE_SUCCESSIONPLANNING &&
                    <Typography sx={{flex: 1}}>
                      Successor Name: {props.report.partner.client_name}
                    </Typography>
                  }
                  <Link
                    href={ `${BACKEND_URL}${props.report.partner.file}` }
                    target="_blank"
                    rel="noopener"
                  >
                    <img src={PdfIcon} style={{ width: "24px" }} />
                  </Link>
                </Box>
              }
            </CardContent>
          </Card>
        }
        {
          !report
          && props.report
          && (props.report.visitor?.source !== siteConstants.SOURCE_COUPLECOACHING)
          && (props.report.visitor?.source !== siteConstants.SOURCE_SUCCESSIONPLANNING)
          &&
          <Box sx={{ width: "100%" }}>
            <LinearProgressWithLabel value={ processProgress } />
          </Box>
        }
        {report && (report.visitor?.source !== siteConstants.SOURCE_COUPLECOACHING) &&
          <>
            <Button
              fullWidth
              variant="contained"
              sx={{ marginTop: "20px" }}
              disabled={
                isCreatingAssessmentReport.value
                || isCreatingAssessmentReportPreview.value
              }
              onClick={ handleCreateAssessmentReport }
            >
              Create Assessment Report
              {(isCreatingAssessmentReport.value || isCreatingAssessmentReportPreview.value) &&
                <>
                  <span>&nbsp;</span>
                  <CircularProgress color="primary" size={16} />
                </>
              }
            </Button>
            <FormControl
              fullWidth
              sx={{ marginTop: "20px", marginBottom: "20px" }}
            >
              <InputLabel id="assess-report-template-label">Assessment Report Template</InputLabel>
              <Select
                labelId="assess-report-template-label"
                label="Assessment Report Template"
                value={selectedTemplateId}
                onChange={handleAssessmentReportTemplateChange}
              >
                {props.assessmentReportTemplates?.map((template, index) => (
                  <MenuItem key={template.id} value={template.id}>{template.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <Editor
              apiKey="tzg0csvxe01ado1p7rnf48ehlnp9k64lxd7r68cjbp2h0824"
              onInit={(evt, editor) => (editorRef.current = editor)}
              id="assessment-report"
              initialValue={assessmentReportPreview}
              init={{
                height: "80vh",
                menubar: true,
                plugins: [
                  "advlist autolink lists link image charmap print preview anchor",
                  "searchreplace visualblocks code fullscreen",
                  "insertdatetime media table paste code help wordcount",
                ],
                toolbar:
                  "undo redo | format select | " +
                  "bold italic backcolor | alignleft aligncenter alignright alignjustify | " +
                  "bullist numlist outdent indent | image | table | help",
                content_style:
                  "body { font-family:Helvetica,Arial,sans-serif; font-size:12px }" +
                  "h4 { font-family:Helvetica,Arial,sans-serif; font-size:10px; font-weight:normal; margin:0 }" +
                  "p { margin: 0; }",
                auto_focus: "assessment-report",
                paste_data_images: true,
                image_title: false,
                image_description: false,
                automatic_uploads: true,
                file_picker_types: "image",
                file_picker_callback: function(cb, value, meta) {
                  const input = document.createElement('input');
                  input.setAttribute('type', 'file');
                  input.setAttribute('accept', 'image/*');
                  input.onchange = function() {
                    const file = this.files[0];
                    const reader = new FileReader();
                    reader.onload = function() {
                      const id = 'blobid' + (new Date()).getTime();
                      const blobCache = editorRef.current.editorUpload.blobCache;
                      const base64 = reader.result.split(',')[1];
                      const blobInfo = blobCache.create(id, file, base64);
                      blobCache.add(blobInfo);
                      cb(blobInfo.blobUri(), {title: file.name});
                    };
                    reader.readAsDataURL(file);
                  };
                  input.click();
                },
              }}
            />
            <Button
              fullWidth
              variant="contained"
              sx={{ marginTop: "20px" }}
              disabled={
                isCreatingAssessmentReport.value
                || isCreatingAssessmentReportPreview.value
              }
              onClick={ handleCreateAssessmentReport }
            >
              Create Assessment Report
              {(isCreatingAssessmentReport.value || isCreatingAssessmentReportPreview.value) &&
                <>
                  <span>&nbsp;</span>
                  <CircularProgress color="primary" size={16} />
                </>
              }
            </Button>
          </>
        }
      </Container>
    </>
  )
}

const mapState = (state) => {
  const {
    isRetrieving,
    report,
    assessmentReportTemplates,
    assessmentReportPreview,
  } = state.cs;
  return {
    isRetrieving,
    report,
    assessmentReportTemplates,
    alert: state.alert,
    assessmentReportPreview,
  }
};

const actionCreators = {
  alertReset: alertActions.reset,
  alertError: alertActions.error,
  retrieveReport: csActions.retrieveReport,
  listAssessmentReportTemplates: csActions.listAssessmentReportTemplates,
  createAssessmentReportPreview: csActions.createAssessmentReportPreview,
};

export default connect(mapState, actionCreators)(AssessmentReportPage);
